/**
 * Kontrakt pluginového systému.
 *
 * Každá hra je jeden adresář v `src/`, jehož název se musí shodovat s `id`
 * v manifestu. Adresář musí exportovat:
 *   - `manifest.ts`  → GameManifest  (čistý TypeScript, bez Reactu)
 *   - `Game.tsx`     → default export GameComponent
 *
 * Registry (`src/registry.ts`) se generuje skriptem `npm run games:sync`,
 * takže přidání hry nevyžaduje úpravu žádného existujícího souboru.
 */
import type { ComponentType } from 'react'

/* ------------------------------------------------------------------ */
/* Parametry hry — z nich se automaticky generuje formulář pro trenéra */
/* ------------------------------------------------------------------ */

interface ParamDefBase {
  /** Klíč v konfiguračním objektu. */
  key: string
  /** Popisek ve formuláři. */
  label: string
  /** Vysvětlení pod polem — co parametr trénuje a jak ho volit. */
  help?: string
  /** Volitelné seskupení polí ve formuláři. */
  group?: string
  /** Parametr se mění jen zřídka; formulář ho schová pod „Pokročilé". */
  advanced?: boolean
}

export interface NumberParamDef extends ParamDefBase {
  type: 'int' | 'float'
  min: number
  max: number
  step: number
  /** Jednotka zobrazená vedle pole, např. „s" nebo „×". */
  unit?: string
  default: number
}

export interface BoolParamDef extends ParamDefBase {
  type: 'bool'
  default: boolean
}

export interface EnumParamDef extends ParamDefBase {
  type: 'enum'
  options: ReadonlyArray<{ value: string; label: string }>
  default: string
}

export type ParamDef = NumberParamDef | BoolParamDef | EnumParamDef

export type GameConfig = Record<string, number | boolean | string>

/* ------------------------------------------------------------------ */
/* Manifest                                                            */
/* ------------------------------------------------------------------ */

export type GameCategory =
  | 'tracking'
  | 'reaction'
  | 'peripheral'
  | 'memory'
  | 'saccades'
  | 'other'

export interface MetricDef {
  key: string
  label: string
  unit?: string
  higherIsBetter: boolean
  /** Počet desetinných míst při zobrazení. */
  precision: number
}

/** Jedna příčka na žebříčku obtížnosti. */
export interface LevelRung {
  level: number
  /** Hodnota, na které level začíná, už naformátovaná k zobrazení. */
  value: string
}

export interface GameManifest {
  /** Musí se shodovat s názvem adresáře. */
  id: string
  name: string
  /** Jedna věta do dlaždice v katalogu. */
  tagline: string
  /** Delší popis: co se trénuje a jak cvičení probíhá. */
  description: string
  version: string
  category: GameCategory
  /** Emoji nebo krátký znak do dlaždice. */
  icon: string
  params: ReadonlyArray<ParamDef>
  /** Metrika, podle které se kreslí graf dlouhodobého progresu. */
  primaryMetric: MetricDef
  /** Doplňkové metriky ukládané ke každé sérii. */
  secondaryMetrics?: ReadonlyArray<MetricDef>

  /**
   * Volitelný přehled stupňů obtížnosti pro dané nastavení. Když ho hra
   * poskytne, zobrazí se klientovi před spuštěním i trenérovi při úpravě
   * programu, takže je dopředu vidět, co který level znamená.
   */
  levels?: (config: GameConfig) => LevelRung[]
}

/* ------------------------------------------------------------------ */
/* Výsledky                                                            */
/* ------------------------------------------------------------------ */

/** Jeden pokus v rámci série. */
export interface TrialRecord {
  index: number
  /** Obtížnost, na které pokus proběhl (u MOT rychlost kuliček). */
  difficulty: number
  /** Byl pokus zcela správný? Jen tehdy se obtížnost zvyšuje. */
  correct: boolean
  /** Kolik cílů klient označil správně. */
  hits: number
  /** Kolik cílů bylo celkem. */
  targets: number
  /** Doba od zastavení do potvrzení odpovědi. */
  responseMs: number
}

export interface GameResult {
  /** Hodnota `primaryMetric` — to, co se trackuje dlouhodobě. */
  score: number
  /** Podíl zcela správných pokusů, 0..1. */
  accuracy: number
  trials: TrialRecord[]
  /** Všechny metriky včetně primární, klíčované podle MetricDef.key. */
  metrics: Record<string, number>
  durationMs: number
  /** Seed generátoru náhody — série je díky němu reprodukovatelná. */
  seed: number
}

/* ------------------------------------------------------------------ */
/* Komponenta hry                                                      */
/* ------------------------------------------------------------------ */

export interface GameProps {
  /** Parametry doplněné o defaulty a ořezané na povolené rozsahy. */
  config: GameConfig
  /** Volá se po dokončení celé série. */
  onFinish: (result: GameResult) => void
  /**
   * Volá se, když klient trénink ukončí předčasně. Hra může předat dosud
   * odehranou část — uloží se jako nedokončená série, aby bylo poznat,
   * že klient trénink začal a nedodělal. Do statistik se nepočítá.
   */
  onAbort: (partial?: GameResult) => void
  /** Průběžný stav pro ukazatel postupu v hlavičce. */
  onProgress?: (p: { trial: number; total: number; difficulty: number }) => void
}

export type GameComponent = ComponentType<GameProps>

export interface GameModule {
  manifest: GameManifest
  Game: GameComponent
}

/* ------------------------------------------------------------------ */
/* Pomocné funkce                                                      */
/* ------------------------------------------------------------------ */

/**
 * Doplní chybějící parametry defaulty a ořízne je na povolené rozsahy.
 * Uložená konfigurace tak přežije i to, že hra později parametr přidá,
 * odebere nebo zúží jeho rozsah.
 */
export function resolveConfig(
  manifest: GameManifest,
  raw: Partial<GameConfig> | null | undefined,
): GameConfig {
  const source = raw ?? {}
  const out: GameConfig = {}

  for (const def of manifest.params) {
    const value = source[def.key]

    switch (def.type) {
      case 'bool':
        out[def.key] = typeof value === 'boolean' ? value : def.default
        break

      case 'enum': {
        const valid = def.options.some((o) => o.value === value)
        out[def.key] = valid ? (value as string) : def.default
        break
      }

      case 'int':
      case 'float': {
        const n = typeof value === 'number' && Number.isFinite(value) ? value : def.default
        const clamped = Math.min(def.max, Math.max(def.min, n))
        out[def.key] = def.type === 'int' ? Math.round(clamped) : clamped
        break
      }
    }
  }

  return out
}

/** Výchozí konfigurace hry — použije se při zakládání nového programu. */
export function defaultConfig(manifest: GameManifest): GameConfig {
  return resolveConfig(manifest, {})
}
